const article = {
	slug: "people-as-microservices",
	title: "People Are Like Microservices",
	date: "4 May 2026",
	updated: null,
	description:
		"The engineering instincts you built shipping distributed systems, load balancing, health checks, backpressure, and circuit breakers, are the same ones that make you a better manager.",
	keywords: [
		"engineering leadership",
		"systems thinking",
		"tech lead",
		"managing engineers",
		"microservices",
	],
	tldr: "A microservice has a defined scope, a health endpoint, and graceful degradation. So should the way you run a team. The hardest distributed system you will ever manage reports to you.",
	readingTime: null,
	provenance:
		"Adapted from my LinkedIn post. The framework traces from David James to Nate Bachmeier to me, by way of Managing Humans by Michael Lopp.",
	provenanceUrl: null,
	series: null,
	artifact: null,
	markdown: `"People are like microservices."

My manager said that in his last one-on-one before leaving for AWS. I smiled and nodded. I had no idea what he meant.

His name was Nate Bachmeier. He was leaving Renaissance Learning. Before he left, he told me I should throw my hat in for his role. I shrank back. I told him I did not have enough experience to manage people.

He smiled. "It's not that hard. You manage them, arrange them, understand them, and then you can grow them properly."

I asked him years later where that line came from. He sent me a link. *Managing Humans* by Michael Lopp. I ordered it that night. It took me a few years to actually understand what he was saying.

## A microservice has a defined scope

You don't call your payment service to handle authentication just because it responds faster. Most managers do exactly this with their best engineer. Same person, every request, every fire. And they are routing based on a map that is two years old. The engineer you hired at v1 is on v3 now. Stale config is still a bug.

## A microservice has a health endpoint

You check it before routing traffic. The one-on-one is that endpoint. One question I ask every Monday: "On a scale of 1 to 10, how full are you?" When a service is overwhelmed it pushes back. People don't. High performers absorb, absorb, absorb, then go dark.

## A well-designed system degrades gracefully

When one service is down, it doesn't stop. It slows. If your team freezes when one person is out, you don't have a team. You have a dependency with no fallback. And if a task has been stuck for two weeks, that is not patience. That is a missing timeout handler.

## The part nobody warned me about

For years I was the single point of failure on my own team. Said yes to everything. No circuit breaker. No queue. Just absorbed. Good architecture never builds that way. I had no business running my team that way either.

Before you hand someone a high-stakes project, you don't flip the switch all at once. Give them a sliver first. Watch how it goes. Full ownership only after the canary holds.

Nate saw something in me before I saw it in myself. I did eventually make that leap, and I brought the engineering instincts with me.

The hardest distributed system you will ever manage reports to you.`,
};

export default article;
